import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Download, Trash2, Edit, Search, FileText, Share2, Eye, Database, Server, Filter } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import ShareModal from '../components/ShareModal';
import './Kubeconfig.css';

interface Kubeconfig {
  id: string;
  name: string;
  description?: string;
  clusterName?: string;
  contextName?: string;
  environment?: string;
  tags: string[];
  notes?: string;
  createdAt: string;
  updatedAt: string;
  createdBy: {
    id: string;
    username: string;
    firstName?: string;
    lastName?: string;
  };
  permissions: {
    canView: boolean;
    canEdit: boolean;
    canDelete: boolean;
  };
}

const Kubeconfig: React.FC = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [kubeconfigs, setKubeconfigs] = useState<Kubeconfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [environmentFilter, setEnvironmentFilter] = useState<string>('all'); // 'all', 'prod', 'staging', 'none'
  const [showModal, setShowModal] = useState(false);
  const [editingKubeconfig, setEditingKubeconfig] = useState<Kubeconfig | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    content: '',
    environment: '',
    tags: [] as string[],
    notes: '',
  });
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [sharingKubeconfig, setSharingKubeconfig] = useState<Kubeconfig | null>(null);

  const fetchKubeconfigs = useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/kubeconfig?${searchTerm ? `search=${encodeURIComponent(searchTerm)}` : ''}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setKubeconfigs(data);
      }
    } catch (error) {
      console.error('Failed to fetch kubeconfigs:', error);
    } finally {
      setLoading(false);
    }
  }, [token, searchTerm]);

  useEffect(() => {
    fetchKubeconfigs();
  }, [fetchKubeconfigs]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (!loading) {
        fetchKubeconfigs();
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm, loading, fetchKubeconfigs]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      if (formData.description) formDataToSend.append('description', formData.description);
      if (formData.environment) formDataToSend.append('environment', formData.environment);
      if (formData.tags.length > 0) formDataToSend.append('tags', JSON.stringify(formData.tags));
      if (formData.notes) formDataToSend.append('notes', formData.notes);

      if (uploadFile) {
        formDataToSend.append('file', uploadFile);
      } else if (formData.content) {
        formDataToSend.append('content', formData.content);
      } else {
        setError('Please provide kubeconfig content (file upload or paste)');
        return;
      }

      const url = editingKubeconfig
        ? `${process.env.REACT_APP_API_URL || '/api'}/kubeconfig/${editingKubeconfig.id}`
        : `${process.env.REACT_APP_API_URL || '/api'}/kubeconfig`;

      const method = editingKubeconfig ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formDataToSend,
      });

      if (response.ok) {
        setShowModal(false);
        setFormData({ name: '', description: '', content: '', environment: '', tags: [], notes: '' });
        setUploadFile(null);
        setEditingKubeconfig(null);
        fetchKubeconfigs();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to save kubeconfig');
      }
    } catch (error: any) {
      setError(error.message || 'Failed to save kubeconfig');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this kubeconfig?')) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/kubeconfig/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        fetchKubeconfigs();
      }
    } catch (error) {
      console.error('Failed to delete kubeconfig:', error);
    }
  };

  const handleDownload = async (id: string, name: string) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/kubeconfig/${id}/download`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${name.replace(/[^a-z0-9]/gi, '_')}_kubeconfig.yaml`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error('Failed to download kubeconfig:', error);
    }
  };

  const handleEdit = (kubeconfig: Kubeconfig) => {
    setEditingKubeconfig(kubeconfig);
    setFormData({
      name: kubeconfig.name,
      description: kubeconfig.description || '',
      content: '',
      environment: kubeconfig.environment || '',
      tags: kubeconfig.tags || [],
      notes: kubeconfig.notes || '',
    });
    setUploadFile(null);
    setShowModal(true);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setUploadFile(e.target.files[0]);
      setFormData({ ...formData, content: '' });
    }
  };

  // Group kubeconfigs by environment
  const groupedKubeconfigs = useMemo(() => {
    const filtered = kubeconfigs.filter((k) => {
      if (environmentFilter === 'all') return true;
      if (environmentFilter === 'none') return !k.environment;
      return k.environment === environmentFilter;
    });

    const grouped: { [key: string]: Kubeconfig[] } = {
      prod: [],
      preprod: [],
      staging: [],
      none: [],
    };

    filtered.forEach((k) => {
      const env = k.environment || 'none';
      if (grouped[env]) {
        grouped[env].push(k);
      } else {
        grouped.none.push(k);
      }
    });

    return grouped;
  }, [kubeconfigs, environmentFilter]);

  const totalCount = kubeconfigs.length;
  const prodCount = kubeconfigs.filter((k) => k.environment === 'prod').length;
  const preprodCount = kubeconfigs.filter((k) => k.environment === 'preprod').length;
  const stagingCount = kubeconfigs.filter((k) => k.environment === 'staging').length;
  const noneCount = kubeconfigs.filter((k) => !k.environment).length;

  if (loading) {
    return <div className="loading">Loading kubeconfigs...</div>;
  }

  return (
    <div className="kubeconfig-page">
      <div className="page-header">
        <h1>Kubeconfig Management</h1>
        {user?.role !== 'STANDARD_USER' && (
          <button className="btn-primary" onClick={() => {
            setEditingKubeconfig(null);
            setFormData({ name: '', description: '', content: '', environment: '', tags: [], notes: '' });
            setUploadFile(null);
            setShowModal(true);
          }}>
            <Plus size={20} />
            Add Kubeconfig
          </button>
        )}
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#3b82f6' }}>
            <Database size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{totalCount}</div>
            <div className="stat-label">Total Kubeconfigs</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#10b981' }}>
            <Server size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{prodCount}</div>
            <div className="stat-label">Production</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#8b5cf6' }}>
            <Server size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{preprodCount}</div>
            <div className="stat-label">Preprod</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#f59e0b' }}>
            <Server size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stagingCount}</div>
            <div className="stat-label">Staging</div>
          </div>
        </div>
      </div>

      <div className="filters-bar">
      <div className="search-bar">
        <Search size={20} />
        <input
          type="text"
          placeholder="Search kubeconfigs..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        </div>
        <div className="environment-filter">
          <Filter size={18} />
          <select
            value={environmentFilter}
            onChange={(e) => setEnvironmentFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Environments</option>
            <option value="prod">Production</option>
            <option value="preprod">Preprod</option>
            <option value="staging">Staging</option>
            <option value="none">No Environment</option>
          </select>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Grouped Display */}
      {(groupedKubeconfigs.prod.length > 0 || groupedKubeconfigs.preprod.length > 0 || groupedKubeconfigs.staging.length > 0 || groupedKubeconfigs.none.length > 0) ? (
        <>
          {groupedKubeconfigs.prod.length > 0 && (
            <div className="environment-group">
              <div className="group-header">
                <Server size={20} />
                <h2>Production ({groupedKubeconfigs.prod.length})</h2>
              </div>
              <div className="kubeconfig-grid">
                {groupedKubeconfigs.prod.map((kubeconfig) => (
                  <div key={kubeconfig.id} className="kubeconfig-card">
                    <div className="card-header">
                      <h3>{kubeconfig.name}</h3>
                      <div className="card-actions">
                        {kubeconfig.permissions.canView && (
                          <>
                            <button
                              className="icon-btn"
                              onClick={() => navigate(`/kubeconfig/${kubeconfig.id}`)}
                              title="View Details"
                            >
                              <Eye size={18} />
                            </button>
                            <button
                              className="icon-btn"
                              onClick={() => {
                                setSharingKubeconfig(kubeconfig);
                                setShowShareModal(true);
                              }}
                              title="Share"
                            >
                              <Share2 size={18} />
                            </button>
                            <button
                              className="icon-btn"
                              onClick={() => handleDownload(kubeconfig.id, kubeconfig.name)}
                              title="Download"
                            >
                              <Download size={18} />
                            </button>
                          </>
                        )}
                        {kubeconfig.permissions.canEdit && (
                          <button
                            className="icon-btn"
                            onClick={() => handleEdit(kubeconfig)}
                            title="Edit"
                          >
                            <Edit size={18} />
                          </button>
                        )}
                        {kubeconfig.permissions.canDelete && (
                          <button
                            className="icon-btn danger"
                            onClick={() => handleDelete(kubeconfig.id)}
                            title="Delete"
                          >
                            <Trash2 size={18} />
                          </button>
                        )}
                      </div>
                    </div>
                    {kubeconfig.description && <p className="card-description">{kubeconfig.description}</p>}
                    <div className="card-info">
                      {kubeconfig.clusterName && (
                        <div className="info-item">
                          <strong>Cluster:</strong> {kubeconfig.clusterName}
                        </div>
                      )}
                      {kubeconfig.contextName && (
                        <div className="info-item">
                          <strong>Context:</strong> {kubeconfig.contextName}
                        </div>
                      )}
                      <div className="info-item">
                        <strong>Created by:</strong> {kubeconfig.createdBy.username}
                      </div>
                      <div className="info-item">
                        <strong>Updated:</strong> {new Date(kubeconfig.updatedAt).toLocaleDateString()}
                      </div>
                    </div>
                    {kubeconfig.tags.length > 0 && (
                      <div className="card-tags">
                        {kubeconfig.tags.map((tag, idx) => (
                          <span key={idx} className="tag">{tag}</span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {groupedKubeconfigs.preprod.length > 0 && (
            <div className="environment-group">
              <div className="group-header">
                <Server size={20} />
                <h2>Preprod ({groupedKubeconfigs.preprod.length})</h2>
              </div>
              <div className="kubeconfig-grid">
                {groupedKubeconfigs.preprod.map((kubeconfig) => (
                  <div key={kubeconfig.id} className="kubeconfig-card">
                    <div className="card-header">
                      <h3>{kubeconfig.name}</h3>
                      <div className="card-actions">
                        {kubeconfig.permissions.canView && (
                          <>
                            <button
                              className="icon-btn"
                              onClick={() => navigate(`/kubeconfig/${kubeconfig.id}`)}
                              title="View Details"
                            >
                              <Eye size={18} />
                            </button>
                            <button
                              className="icon-btn"
                              onClick={() => {
                                setSharingKubeconfig(kubeconfig);
                                setShowShareModal(true);
                              }}
                              title="Share"
                            >
                              <Share2 size={18} />
                            </button>
                            <button
                              className="icon-btn"
                              onClick={() => handleDownload(kubeconfig.id, kubeconfig.name)}
                              title="Download"
                            >
                              <Download size={18} />
                            </button>
                          </>
                        )}
                        {kubeconfig.permissions.canEdit && (
                          <button
                            className="icon-btn"
                            onClick={() => handleEdit(kubeconfig)}
                            title="Edit"
                          >
                            <Edit size={18} />
                          </button>
                        )}
                        {kubeconfig.permissions.canDelete && (
                          <button
                            className="icon-btn danger"
                            onClick={() => handleDelete(kubeconfig.id)}
                            title="Delete"
                          >
                            <Trash2 size={18} />
                          </button>
                        )}
                      </div>
                    </div>
                    {kubeconfig.description && <p className="card-description">{kubeconfig.description}</p>}
                    <div className="card-info">
                      {kubeconfig.clusterName && (
                        <div className="info-item">
                          <strong>Cluster:</strong> {kubeconfig.clusterName}
                        </div>
                      )}
                      {kubeconfig.contextName && (
                        <div className="info-item">
                          <strong>Context:</strong> {kubeconfig.contextName}
                        </div>
                      )}
                      <div className="info-item">
                        <strong>Created by:</strong> {kubeconfig.createdBy.username}
                      </div>
                      <div className="info-item">
                        <strong>Updated:</strong> {new Date(kubeconfig.updatedAt).toLocaleDateString()}
                      </div>
                    </div>
                    {kubeconfig.tags.length > 0 && (
                      <div className="card-tags">
                        {kubeconfig.tags.map((tag, idx) => (
                          <span key={idx} className="tag">{tag}</span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {groupedKubeconfigs.staging.length > 0 && (
            <div className="environment-group">
              <div className="group-header">
                <Server size={20} />
                <h2>Staging ({groupedKubeconfigs.staging.length})</h2>
              </div>
              <div className="kubeconfig-grid">
                {groupedKubeconfigs.staging.map((kubeconfig) => (
                  <div key={kubeconfig.id} className="kubeconfig-card">
                    <div className="card-header">
                      <h3>{kubeconfig.name}</h3>
                      <div className="card-actions">
                        {kubeconfig.permissions.canView && (
                          <>
                            <button
                              className="icon-btn"
                              onClick={() => navigate(`/kubeconfig/${kubeconfig.id}`)}
                              title="View Details"
                            >
                              <Eye size={18} />
                            </button>
                            <button
                              className="icon-btn"
                              onClick={() => {
                                setSharingKubeconfig(kubeconfig);
                                setShowShareModal(true);
                              }}
                              title="Share"
                            >
                              <Share2 size={18} />
                            </button>
                            <button
                              className="icon-btn"
                              onClick={() => handleDownload(kubeconfig.id, kubeconfig.name)}
                              title="Download"
                            >
                              <Download size={18} />
                            </button>
                          </>
                        )}
                        {kubeconfig.permissions.canEdit && (
                          <button
                            className="icon-btn"
                            onClick={() => handleEdit(kubeconfig)}
                            title="Edit"
                          >
                            <Edit size={18} />
                          </button>
                        )}
                        {kubeconfig.permissions.canDelete && (
                          <button
                            className="icon-btn danger"
                            onClick={() => handleDelete(kubeconfig.id)}
                            title="Delete"
                          >
                            <Trash2 size={18} />
                          </button>
                        )}
                      </div>
                    </div>
                    {kubeconfig.description && <p className="card-description">{kubeconfig.description}</p>}
                    <div className="card-info">
                      {kubeconfig.clusterName && (
                        <div className="info-item">
                          <strong>Cluster:</strong> {kubeconfig.clusterName}
                        </div>
                      )}
                      {kubeconfig.contextName && (
                        <div className="info-item">
                          <strong>Context:</strong> {kubeconfig.contextName}
                        </div>
                      )}
                      <div className="info-item">
                        <strong>Created by:</strong> {kubeconfig.createdBy.username}
                      </div>
                      <div className="info-item">
                        <strong>Updated:</strong> {new Date(kubeconfig.updatedAt).toLocaleDateString()}
                      </div>
                    </div>
                    {kubeconfig.tags.length > 0 && (
                      <div className="card-tags">
                        {kubeconfig.tags.map((tag, idx) => (
                          <span key={idx} className="tag">{tag}</span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {groupedKubeconfigs.none.length > 0 && environmentFilter !== 'all' && (
            <div className="environment-group">
              <div className="group-header">
                <Server size={20} />
                <h2>No Environment ({groupedKubeconfigs.none.length})</h2>
              </div>
      <div className="kubeconfig-grid">
                {groupedKubeconfigs.none.map((kubeconfig) => (
          <div key={kubeconfig.id} className="kubeconfig-card">
            <div className="card-header">
              <h3>{kubeconfig.name}</h3>
              <div className="card-actions">
                {kubeconfig.permissions.canView && (
                  <>
                    <button
                      className="icon-btn"
                      onClick={() => navigate(`/kubeconfig/${kubeconfig.id}`)}
                      title="View Details"
                    >
                      <Eye size={18} />
                    </button>
                    <button
                      className="icon-btn"
                      onClick={() => {
                        setSharingKubeconfig(kubeconfig);
                        setShowShareModal(true);
                      }}
                      title="Share"
                    >
                      <Share2 size={18} />
                    </button>
                    <button
                      className="icon-btn"
                      onClick={() => handleDownload(kubeconfig.id, kubeconfig.name)}
                      title="Download"
                    >
                      <Download size={18} />
                    </button>
                  </>
                )}
                {kubeconfig.permissions.canEdit && (
                  <button
                    className="icon-btn"
                    onClick={() => handleEdit(kubeconfig)}
                    title="Edit"
                  >
                    <Edit size={18} />
                  </button>
                )}
                {kubeconfig.permissions.canDelete && (
                  <button
                    className="icon-btn danger"
                    onClick={() => handleDelete(kubeconfig.id)}
                    title="Delete"
                  >
                    <Trash2 size={18} />
                  </button>
                )}
              </div>
            </div>
            {kubeconfig.description && <p className="card-description">{kubeconfig.description}</p>}
            <div className="card-info">
              {kubeconfig.clusterName && (
                <div className="info-item">
                  <strong>Cluster:</strong> {kubeconfig.clusterName}
                </div>
              )}
              {kubeconfig.contextName && (
                <div className="info-item">
                  <strong>Context:</strong> {kubeconfig.contextName}
                </div>
              )}
              <div className="info-item">
                <strong>Created by:</strong> {kubeconfig.createdBy.username}
              </div>
              <div className="info-item">
                <strong>Updated:</strong> {new Date(kubeconfig.updatedAt).toLocaleDateString()}
              </div>
            </div>
            {kubeconfig.tags.length > 0 && (
              <div className="card-tags">
                {kubeconfig.tags.map((tag, idx) => (
                  <span key={idx} className="tag">{tag}</span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
            </div>
          )}
        </>
      ) : null}

      {totalCount === 0 && (
        <div className="empty-state">
          <FileText size={48} />
          <p>No kubeconfigs found</p>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editingKubeconfig ? 'Edit Kubeconfig' : 'Add Kubeconfig'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                />
              </div>
              <div className="form-group">
                <label>Environment</label>
                <select
                  value={formData.environment}
                  onChange={(e) => setFormData({ ...formData, environment: e.target.value })}
                >
                  <option value="">Select Environment</option>
                  <option value="prod">Production</option>
                  <option value="preprod">Preprod</option>
                  <option value="staging">Staging</option>
                </select>
              </div>
              <div className="form-group">
                <label>Upload File or Paste Content</label>
                <input
                  type="file"
                  accept=".yaml,.yml,.json"
                  onChange={handleFileChange}
                  disabled={!!formData.content}
                />
                {!uploadFile && (
                  <textarea
                    placeholder="Or paste kubeconfig content here..."
                    value={formData.content}
                    onChange={(e) => {
                      setFormData({ ...formData, content: e.target.value });
                      setUploadFile(null);
                    }}
                    rows={10}
                    style={{ marginTop: '10px', fontFamily: 'monospace' }}
                  />
                )}
                {uploadFile && (
                  <div className="file-info">
                    <FileText size={16} />
                    <span>{uploadFile.name}</span>
                    <button
                      type="button"
                      onClick={() => setUploadFile(null)}
                      className="btn-link"
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>
              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                />
              </div>
              {error && <div className="error-message">{error}</div>}
              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingKubeconfig ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showShareModal && sharingKubeconfig && (
        <ShareModal
          kubeconfigId={sharingKubeconfig.id}
          kubeconfigName={sharingKubeconfig.name}
          resourceType="kubeconfig"
          onClose={() => {
            setShowShareModal(false);
            setSharingKubeconfig(null);
          }}
          onSuccess={() => {
            fetchKubeconfigs();
          }}
        />
      )}
    </div>
  );
};

export default Kubeconfig;

